import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, message, Input, Grid, Space, Flex, Card, Tooltip, Popover, Segmented } from 'antd';
import { PlusOutlined, SearchOutlined, EllipsisOutlined, AppstoreOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import TicketDetailModal from './TicketDetail/TicketDetail';
import debounce from 'lodash/debounce';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { getTickets, createTicket, updateTicket, deleteTicket } from './TicketServices';
import KanbanBoard from './TicketList/TicketList';
import TicketTableView from './TicketTableView/TicketTableView';
import CreateTicketModal from './CreateTicketModal/CreateTicketModal';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import { useTranslation } from 'react-i18next';
import useSocket from '../../Hooks/useSocket';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import useSocketEvent from '../../Hooks/useSocketEvent';
import { Resource, Action } from '../../Constants/socket';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import SharedFilterBar from '../Shared/Filters/SharedFilterBar';
import { ticketFilterFields } from '../Shared/Filters/filterConfig';
import ExportButton from '../DataExportImport/ExportButton';
import ImportButton from '../DataExportImport/ImportButton';
import './Tickets.css';

const { useBreakpoint } = Grid;

const VIEW_STORAGE_KEY = 'ticket_view_preference';

const TicketsPage = () => {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const { id: routeTicketId } = useParams();
    const navigate = useNavigate();
    const [viewTicketId, setViewTicketId] = useState(routeTicketId);

    useEffect(() => {
        setViewTicketId(routeTicketId);
    }, [routeTicketId]);

    const handleCloseTicketModal = useCallback(() => {
        setViewTicketId(null);
        navigate('/tickets');
    }, [navigate]);
    const { setLoading, user: { id: currentUserId } = {} } = useGlobal();
    const [skeleton, setSkeleton] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { handleError } = useErrorHandler();
    const [tickets, setTickets] = useState([]);
    const [searchText, setSearchText] = useState('');
    const searchTextRef = React.useRef('');
    const fetchAbortControllerRef = React.useRef(null);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [modalMode, setModalMode] = useState('create');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [filters, setFilters] = useState([]);
    const [normalizedFilters, setNormalizedFilters] = useState({});
    const [sortBy, setSortBy] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [isMoreActionsOpen, setIsMoreActionsOpen] = useState(false);
    const { connectionState } = useSocket();
    const isSubscribed = useSocketSubscription(Resource.USER, currentUserId ? [currentUserId] : null, true);
    const ticketIds = useMemo(() => tickets.map(ticket => ticket.id), [tickets]);
    useSocketSubscription(Resource.TICKET, ticketIds);

    // View preference: 'board' or 'list'
    const [viewMode, setViewMode] = useState(() => {
        try {
            const saved = localStorage.getItem(VIEW_STORAGE_KEY);
            if (saved === 'board' || saved === 'list') return saved;
        } catch { }
        return 'board';
    });
    const isCompact = !screens.md;

    const handleViewChange = useCallback((value) => {
        setViewMode(value);
        try {
            localStorage.setItem(VIEW_STORAGE_KEY, value);
        } catch { }
    }, []);

    /**
     * For our Kanban board we fetch a larger page to show all relevant tickets.
     * The API still paginates but we request a generous page size.
     */
    const fetchTickets = useCallback(async (search = '', filtersPayload = {}) => {
        if (fetchAbortControllerRef.current) {
            fetchAbortControllerRef.current.abort();
        }
        const controller = new AbortController();
        fetchAbortControllerRef.current = controller;

        try {
            setLoading(true);
            const { data } = await getTickets({
                page: 1,
                size: 100, // Max allowed by API
                search: search.trim(),
                filters: filtersPayload,
                sort_by: sortBy,
                sort_direction: sortDirection,
                signal: controller.signal,
            });
            setTickets(data || []);
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
            handleError(error);
        } finally {
            if (fetchAbortControllerRef.current === controller) {
                setLoading(false);
                setSkeleton(false);
            }
        }
    }, [setLoading, handleError, sortBy, sortDirection]);

    useEffect(() => {
        return () => {
            if (fetchAbortControllerRef.current) {
                fetchAbortControllerRef.current.abort();
            }
        };
    }, []);

    // Socket event handlers
    const handleTicketEvent = useCallback(() => {
        fetchTickets(searchTextRef.current, normalizedFilters);
    }, [fetchTickets, normalizedFilters]);

    const handleTicketUpdated = useCallback((data) => {
        setTickets(prev =>
            prev.map(ticket => ticket.id === data.id ? { ...ticket, ...data } : ticket)
        );
    }, []);

    useSocketEvent(Resource.TICKET, Action.CREATE, handleTicketEvent);
    useSocketEvent(Resource.TICKET, Action.UPDATE, handleTicketUpdated);
    useSocketEvent(Resource.TICKET, Action.DELETE, handleTicketEvent);

    const debouncedSearch = useCallback(
        debounce((searchValue, filtersPayload) => {
            setSkeleton(true);
            fetchTickets(searchValue, filtersPayload);
        }, 500),
        [fetchTickets]
    );

    const debouncedFilterChange = useCallback(
        debounce((search, filtersPayload) => {
            setSkeleton(true);
            fetchTickets(search, filtersPayload);
        }, 500),
        [fetchTickets]
    );

    useEffect(() => {
        fetchTickets(searchTextRef.current, normalizedFilters);
    }, [fetchTickets]);

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchText(value);
        searchTextRef.current = value;
        debouncedSearch(value, normalizedFilters);
    }, [debouncedSearch, normalizedFilters]);

    const handleFiltersChange = useCallback((nextFilters, normalized) => {
        setFilters(nextFilters);
        setNormalizedFilters(normalized);
        debouncedFilterChange(searchTextRef.current, normalized);
    }, [debouncedFilterChange]);

    const handleSortChange = useCallback(({ sort_by, sort_direction }) => {
        setSortBy(sort_by || 'created_at');
        setSortDirection(sort_by ? (sort_direction || 'desc') : 'desc');
        setSkeleton(true);
    }, []);

    /**
     * Called when a card is dragged to a new column on the Kanban board.
     * Optimistically updates the local state, then syncs with the backend.
     */
    const handleStatusChange = useCallback(async (ticketId, updates) => {
        // Optimistic update
        setTickets(prev =>
            prev.map(ticket =>
                ticket.id === ticketId
                    ? { ...ticket, ...updates }
                    : ticket
            )
        );

        try {
            const payload = await updateTicket(ticketId, updates);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
        } catch (error) {
            // Revert on failure
            fetchTickets(searchTextRef.current, normalizedFilters);
            handleError(error);
        }
    }, [updateTicket, fetchTickets, normalizedFilters, handleError, t]);

    const handleTicketCreate = useCallback(async (ticketData, finish) => {
        try {
            setLoading(true);
            const payload = await createTicket(ticketData);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            fetchTickets(searchTextRef.current, normalizedFilters);
            setIsModalVisible(false);
            setSelectedTicket(null);
            setModalMode('create');
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
            if (finish) finish();
        }
    }, [normalizedFilters, fetchTickets, setLoading, handleError, t]);

    const handleClose = () => {
        setIsModalVisible(false);
        setSelectedTicket(null);
        setModalMode('create');
    };

    return (
        <Flex
            vertical
            style={{
                flexGrow: 1,
                padding: 0,
                height: '100%',
                overflow: 'hidden',
            }}
        >
            {/* Toolbar */}
            <Card
                style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', flexShrink: 0 }}
                title={t('tickets')}
                extra={
                    <Space size={4}>
                        {/* View toggle */}
                        <Segmented
                            size="small"
                            value={viewMode}
                            onChange={handleViewChange}
                            options={[
                                {
                                    value: 'board',
                                    icon: <AppstoreOutlined />,
                                },
                                {
                                    value: 'list',
                                    icon: <UnorderedListOutlined />,
                                },
                            ]}
                        />
                        <Button
                            type="link"
                            icon={<PlusOutlined />}
                            size="medium"
                            onClick={() => setIsModalVisible(true)}
                        >
                            {!screens.xs && t('create_ticket')}
                        </Button>
                        <Popover
                            trigger="click"
                            placement="bottomRight"
                            open={isMoreActionsOpen}
                            onOpenChange={setIsMoreActionsOpen}
                            content={
                                <Space direction="vertical" style={{ minWidth: 140 }} onClickCapture={() => setIsMoreActionsOpen(false)}>
                                    <ImportButton resource={Resource.TICKET} type="text" size="small" />
                                    <ExportButton resource={Resource.TICKET} filters={normalizedFilters} type="text" size="small" />
                                </Space>
                            }
                        >
                            <Tooltip title={t('more_actions')}>
                                <Button type="text" icon={<EllipsisOutlined />} size="medium" />
                            </Tooltip>
                        </Popover>
                    </Space>
                }
                styles={{ body: { padding: 12 } }}
            >
                <Flex gap="middle" wrap="wrap" align="center" justify="space-between">
                    <Flex flex={{ xs: '100%', md: 1 }} style={{ flexGrow: 1 }}>
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder={t('search_tickets')}
                            size="medium"
                            onChange={handleSearchChange}
                            onFocus={() => setIsInputFocused(true)}
                            onBlur={() => setIsInputFocused(false)}
                            value={searchText}
                            style={{
                                width: isInputFocused ? '100%' : (screens.xs ? '100%' : '400px'),
                                transition: 'width 0.4s ease-in-out'
                            }}
                        />
                    </Flex>

                    <SharedFilterBar
                        fields={ticketFilterFields}
                        value={filters}
                        onChange={handleFiltersChange}
                        sortFields={[
                            { value: 'created_at', labelKey: 'created_at' },
                            { value: 'updated_at', labelKey: 'updated_at' },
                            { value: 'priority', labelKey: 'ticket_priority' },
                            { value: 'category', labelKey: 'ticket_category' },
                        ]}
                        sortValue={{ sort_by: sortBy, sort_direction: sortDirection }}
                        onSortChange={handleSortChange}
                    />
                </Flex>
            </Card>

            {/* Content: Board or List */}
            <div style={{ flex: 1, marginTop: 12, minHeight: 0, overflow: 'hidden' }}>
                {viewMode === 'board' ? (
                    <KanbanBoard
                        tickets={tickets}
                        onStatusChange={handleStatusChange}
                        skeleton={skeleton}
                    />
                ) : (
                    <div style={{ height: '100%', overflow: 'auto', padding: '0 4px' }}>
                        <TicketTableView
                            tickets={tickets}
                            onStatusChange={handleStatusChange}
                            skeleton={skeleton}
                        />
                    </div>
                )}
            </div>

            {viewTicketId && (
                <TicketDetailModal
                    ticketId={viewTicketId}
                    onClose={handleCloseTicketModal}
                />
            )}
            {isModalVisible && (
                <CreateTicketModal
                    onClose={handleClose}
                    onSubmit={handleTicketCreate}
                    mode={modalMode}
                    initialData={selectedTicket}
                />
            )}
        </Flex>
    );
};

export default TicketsPage;
